import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

export default function VerifyEmail() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (user === false) {
      navigate("/login");
      return;
    }
    if (user && user.email_verified !== false) {
      const hasPending = !!sessionStorage.getItem("inlight_current_prayer");
      navigate(hasPending ? "/prayer" : "/builder");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { code });
      await refresh();
      const hasPending = !!sessionStorage.getItem("inlight_current_prayer");
      navigate(hasPending ? "/prayer" : "/builder");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError("");
    setNotice("");
    try {
      await api.post("/auth/resend-verification");
      setNotice("A fresh code is on its way to your inbox.");
      setCooldown(60);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
      <Header />
      <div className="sanctuary-glow absolute inset-x-0 top-16 h-[420px] -z-0 pointer-events-none" />
      <div className="relative max-w-md mx-auto px-6 pt-20 md:pt-28">
        <h1
          className="font-serif-display text-4xl md:text-5xl mb-3 tracking-tight"
          style={{ color: "var(--sanctuary-ink)" }}
          data-testid="verify-title"
        >
          One last candle to light
        </h1>
        <p className="mb-2 text-base" style={{ color: "var(--sanctuary-ink-soft)" }} data-testid="verify-subtitle">
          We sent a 6-digit code to <span style={{ color: "var(--sanctuary-ink)" }}>{user.email}</span>.
        </p>
        <p className="mb-12 text-sm" style={{ color: "var(--sanctuary-ink-muted)" }}>
          It may take a minute — check your spam folder too.
        </p>

        <form onSubmit={submit} className="space-y-10">
          <div>
            <label className="block text-xs tracking-widest uppercase mb-3" style={{ color: "var(--sanctuary-ink-muted)" }}>
              Verification code
            </label>
            <input
              data-testid="verify-code-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="input-line text-center text-2xl tracking-[0.6em]"
              placeholder="••••••"
            />
          </div>

          {error && (
            <p data-testid="verify-error" className="text-sm" style={{ color: "var(--sanctuary-alert)" }}>
              {error}
            </p>
          )}
          {notice && (
            <p data-testid="verify-notice" className="text-sm" style={{ color: "#7A8A6F" }}>
              {notice}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || code.length !== 6}
            data-testid="verify-submit-button"
            className="w-full px-8 py-4 rounded-full btn-primary text-base disabled:opacity-60"
          >
            {busy ? "Lighting…" : "Verify my email"}
          </button>
        </form>

        <div className="mt-10 text-sm" style={{ color: "var(--sanctuary-ink-soft)" }}>
          Didn't receive it?{" "}
          <button
            type="button"
            onClick={resend}
            disabled={cooldown > 0}
            data-testid="verify-resend-button"
            className="underline disabled:opacity-50 disabled:no-underline"
            style={{ color: "var(--sanctuary-accent)" }}
          >
            {cooldown > 0 ? `Send a new code (${cooldown}s)` : "Send a new code"}
          </button>
        </div>
      </div>
    </div>
  );
}
